import type { AutomationRunOpenTarget } from '@/lib/automation/runHistory';
import { createContext, useContext } from 'react';
import type { Roles } from '@/constants/roles';
import type { PortalCapabilities } from '../session/capabilities';
import type {
  PortalActor,
  PortalEffectiveSubject,
  ImpersonationPartnerOption,
} from '../session/portalSession';
import type { PortalNavigationHandlerRegistration } from '../navigation';
import type { QuizWorkspaceInitialTarget } from '../hooks/useQuizWorkspaceSession';
import type { QuizCreationBasis } from '../utils/quizCreationFlowOptions';

export type PortalViewContextValue = {
  actor: PortalActor | null;
  viewAsOptions: ImpersonationPartnerOption[];
  capabilities: PortalCapabilities;
  effective: PortalEffectiveSubject | null;
  effectivePartnerId: string | null;
  effectiveRole: Roles;
  tabRole: 'admin' | 'partner';
  canBrowseUnscopedAdminQuizzes: boolean;
  hasAdvancedAnalytics: boolean;
  analyticsMarkViewedBehavior: 'partner' | 'admin-acting' | 'none';
  stealthModeEnabled: boolean;
  setStealthModeEnabled(enabled: boolean): void;
  isMobileLayout: boolean;
  activeView: string;
  activePollDefinitionId: string | null;
  libraryNotice: string | null;
  showLibraryRootRequestKey: number;
  dismissRecentBadge(key: string): void;
  registerNavigationHandler(view: string): PortalNavigationHandlerRegistration;
  setActiveView(view: string): void;
  requestActiveViewChange(view: string): Promise<boolean>;
  handleOpenLibrary(): Promise<void>;
  handleStartNewDraft(
    target?: { quizId: string; editionId: string },
    basis?: QuizCreationBasis
  ): Promise<void>;
  handleOpenQuizWorkspace(target: Extract<QuizWorkspaceInitialTarget, { kind: 'quiz' }>): void;
  handleQuizWorkspaceLoadDenied(): void;
  handleCloseQuizWorkspace(): void;
  handleQuizWorkspaceTargetTransition(target: QuizWorkspaceInitialTarget): void;
  handleOpenPollWorkspace(pollDefinitionId: string): void;
  handleClosePollWorkspace(): void;
  isQuizCreationBasisRequired: boolean;
  handleRequestQuizCreationBasis(): void;
  handleCloseQuizCreationBasis(): void;
  handleSelectQuizCreationBasis(basis: QuizCreationBasis): void;
  onNavigate(view: string): void;
  onOpenRun(target: AutomationRunOpenTarget): void;
};

export const portalViewContext = createContext<PortalViewContextValue | null>(null);

export function usePortalViewContext(): PortalViewContextValue {
  const context = useContext(portalViewContext);
  if (!context) {
    throw new Error('usePortalViewContext must be used inside PortalViewProvider');
  }
  return context;
}
